import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {colors, useAddressContext, useCartContext} from 'components/providers';
import {AlertDialog} from 'components/shared';
import {useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {FC, useEffect, useState} from 'react';
import {IAddress} from 'types';
import ImageUI from 'ui/ImageUI';
import {getAddressDetails, getStreetNameFromAddress} from 'utils/addresses';

import {BtnWithPlus} from '../../../pages/checkout/BtnWithPlus';
import {TitledSection} from '.';
import {IconBlock} from './IconBlock';

interface Props {
  editable?: boolean;
  address?: IAddress;
  adressId?: number | string;
  isFromCheckout?: boolean;
  showError?: boolean;
  showPopover?: boolean;
  isAddressesLoading?: boolean;
  onClosePopover?: () => void;
}

const icon = '/icons/address-type/pin-icon.svg';

export const OrderAddress: FC<Props> = ({
  editable = false,
  address,
  isFromCheckout,
  showError,
  adressId,
  showPopover,
  isAddressesLoading,
  onClosePopover,
}) => {
  const {t} = useTranslation();
  const [isOpeAlertDialog, setIsOpeAlertDialog] = useState(false);
  const {clearCart} = useCartContext();
  const {openAddAddressModal} = useAddressContext();
  const router = useRouter();

  const {isOpen, onOpen, onClose} = useDisclosure();

  useEffect(() => {
    if (showPopover) onOpen();
  }, [onOpen, showPopover]);

  const onChangeIsOpeAlertDialog = () => {
    setIsOpeAlertDialog((prev) => !prev);
  };

  const handleAlertConfirm = () => {
    clearCart();
    router.push('/');
  };

  return (
    <TitledSection title={t('ADDRESS_DETAILS')}>
      {isOpen && (
        <Box position="fixed" top={0} left={0} width="100vw" height="100vh" bg="rgba(0, 0, 0, 0.5)" zIndex="overlay" />
      )}

      <Flex
        bg="white"
        borderRadius="16px"
        alignItems="center"
        justifyContent="space-between"
        borderColor={showError ? colors.red : ''}
        borderWidth={showError ? 1 : 0}
        zIndex={isOpen ? 'modal' : 'auto'}
        position="relative"
      >
        {isAddressesLoading ? (
          <Flex direction="row" justifyContent="center" alignItems="center" w="full">
            <Spinner justifyContent="center" />
          </Flex>
        ) : (
          <>
            {address ? (
              <IconBlock icon={icon}>
                <Flex
                  flexDirection={['column', 'row']}
                  alignItems={['stretch', 'center']}
                  justifyContent="space-between"
                >
                  <Box>
                    <Text fontSize="16px" fontWeight="600" color={colors.lightBlack} mb="5px">
                      {getStreetNameFromAddress(address)}
                    </Text>
                    <Text fontSize="14px" fontWeight="500" color={colors.textGray}>
                      {getAddressDetails(address, t)}
                    </Text>
                    {address?.customer_address_comment && (
                      <Text fontSize="16px" fontWeight="500" mt="8px" color={colors.textGray}>
                        *{address.customer_address_comment}
                      </Text>
                    )}
                  </Box>
                  {editable && (
                    <Box position="relative" mt="15px">
                      <BtnWithPlus title={t('FORCE_CHANGE_ADDRESS')} onClick={onChangeIsOpeAlertDialog} />
                    </Box>
                  )}
                  <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
                    <PopoverTrigger>
                      {isFromCheckout ? (
                        <Button
                          px="20px"
                          mt="15px"
                          variant="outline"
                          _focus={{borderColor: colors.black}}
                          leftIcon={<ImageUI w="1rem" h="1rem" src="/icons/plus.svg" alt="Plus" />}
                          onClick={() => {
                            openAddAddressModal(adressId);
                            onClosePopover && onClosePopover();
                          }}
                        >
                          {t('FORCE_CHANGE_ADDRESS')}
                        </Button>
                      ) : (
                        <></>
                      )}
                    </PopoverTrigger>
                    <PopoverContent zIndex="modal" w={['xs', 'sm']} _focus={{outline: 'none'}}>
                      <PopoverArrow />
                      <PopoverCloseButton />
                      <PopoverHeader p={4} fontWeight="bold">
                        {t('askForAddress')}
                      </PopoverHeader>
                      <PopoverFooter display="flex" justifyContent="flex-end">
                        <Button
                          variant="outline"
                          mr={2}
                          onClick={() => {
                            openAddAddressModal(adressId);
                            onClosePopover && onClosePopover();
                            onClose();
                          }}
                        >
                          {t('FORCE_CHANGE_ADDRESS')}
                        </Button>
                        <Button variant="primary" onClick={onClose}>
                          {t('yes')}
                        </Button>
                      </PopoverFooter>
                    </PopoverContent>
                  </Popover>
                </Flex>
              </IconBlock>
            ) : (
              <Container variant="white-block" w="full" p={4}>
                <Flex direction="row" justifyContent="space-between" alignItems="center">
                  <Heading as="h3" fontSize="14px" mr="5px">
                    {t('addressNotSelected')}
                  </Heading>
                  <Button
                    fontSize="14px"
                    w={['full', 'auto']}
                    px={['30px', '50px']}
                    variant="outline"
                    _focus={{borderColor: colors.black}}
                    leftIcon={<ImageUI w="1rem" h="1rem" src="/icons/plus.svg" alt="Plus" />}
                    onClick={() => openAddAddressModal()}
                  >
                    {t('ADD_NEW_ADDRESS')}
                  </Button>
                </Flex>
              </Container>
            )}
          </>
        )}

        <AlertDialog
          title={t('ALERT_CHANGE_ADDRESS')}
          isOpen={isOpeAlertDialog}
          onClose={onChangeIsOpeAlertDialog}
          onConfirm={handleAlertConfirm}
        >
          {t('ALERT_CHANGE_ADDRESS_MESSAGE')}
        </AlertDialog>
      </Flex>
    </TitledSection>
  );
};
